// src/hooks/useVersionFrontThumbnail.js
// Returns a data URL of a version's front-cover, rendered from its
// wraparoundDesign JSON via off-screen Fabric. Cached in-memory across the
// session so the same (versionId, updatedAt) pair only renders once.

import { useEffect, useState } from 'react';
import { renderFrontCoverThumbnail } from '../utils/renderFrontCoverThumbnail';

const cache = new Map(); // key: `${id}::${updatedAt}` → dataURL

function cacheKey(version) {
  const stamp = version?.updatedAt ? new Date(version.updatedAt).getTime() : '';
  return `${version?.id}::${stamp}`;
}

/**
 * @param {object} version - the version object (must include wraparoundDesign for a render)
 * @param {object} [opts]
 * @param {number} [opts.targetWidth=200] - desired thumbnail width in pixels
 * @returns {string|null} data URL or null while loading/unavailable
 */
export default function useVersionFrontThumbnail(version, opts = {}) {
  const targetWidth = opts.targetWidth || 200;
  const [url, setUrl] = useState(() => cache.get(cacheKey(version)) || null);

  useEffect(() => {
    let cancelled = false;
    if (!version) return undefined;
    if (!version.wraparoundDesign) return undefined;

    const k = cacheKey(version);
    const cached = cache.get(k);
    if (cached) {
      setUrl(cached);
      return undefined;
    }

    (async () => {
      const dataUrl = await renderFrontCoverThumbnail(
        version.wraparoundDesign,
        version,
        { targetWidth },
      );
      if (cancelled) return;
      if (dataUrl) {
        cache.set(k, dataUrl);
        setUrl(dataUrl);
      }
    })();

    return () => { cancelled = true; };
  }, [version, targetWidth]);

  return url;
}
